import {assert} from 'Asserts';
import {TargetFile} from 'folder-items/FileConstants';
import {
  ComplexPolymerAttachmentStyle,
  GQLComplexPolymerType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CreateMenuComplexPolymersQuery} from 'modbio/complex-polymers/__generated/CreateMenuComplexPolymersQuery';
import {
  ComplexPolymerConnection,
  ComplexPolymerGraphQL,
  ComplexPolymerInput,
  ComplexPolymerType,
  ComplexPolymerVersionGraphQL,
  ConjugateConnectionSideGraphQL,
  ConnectionGraphQL,
  EntityAssociations,
  HELMPolymerType,
  MODEL_TYPE_TO_HELM_POLYMER_TYPE,
  MoleculeSimplePolymerGraphQL,
  OLIGO_CONJUGATE_BACKBONE_STRAND_REF,
  OLIGO_DUPLEX_FORWARD_STRAND_REF,
  OLIGO_DUPLEX_REVERSE_STRAND_REF,
  OligoConjugateGraphQL,
  OligoConjugateVersionGraphQL,
  OligoDuplexGraphQL,
  OligoDuplexOligoStrandGraphQL,
  OligoDuplexVersionGraphQL,
  OligoSimplePolymerGraphQL,
  ResolvedAttachmentGraphQL,
  ResolvedAttachmentInput,
  SimplePolymerGraphQL,
  SourceLinkedMoleculeConnectionSideGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {CreateComplexPolymerSchemaTag} from 'modbio/complex-polymers/create/CreateComplexPolymerUtil';
import {getStore} from 'redux-stores/Store';
import {
  CreateAndRegisterBioentityArgs,
  CreateBioentityArgs,
} from 'registry/create-bioentity/CreateBioentityUtil';
import APIUtil from 'util/APIUtil';

import {graphqlSchemaInterfaceToComplexPolymerType} from './ComplexPolymerSchemaInterfaceConstants';
import {ConjugateConnection} from './tabs/ComplexPolymerComponentsSummaryUtil';

export type ComplexPolymerSchemaConfigGraphQL =
  CreateMenuComplexPolymersQuery['complexPolymerSchemaConfigurations'][0];
export type OligoConjugateSchemaConfigGraphQL = ExtractByTypename<
  CreateMenuComplexPolymersQuery['complexPolymerSchemaConfigurations'][0],
  'OligoConjugateSchemaConfigNode'
>;
export type OligoDuplexSchemaConfigGraphQL = ExtractByTypename<
  CreateMenuComplexPolymersQuery['complexPolymerSchemaConfigurations'][0],
  'OligoDuplexSchemaConfigNode'
>;

export function isOligoConjugate(
  complexPolymer: OligoConjugateGraphQL | OligoDuplexGraphQL | ComplexPolymerGraphQL
): complexPolymer is OligoConjugateGraphQL {
  return complexPolymer.__typename === 'OligoConjugateNode';
}

export function isOligoDuplex(
  complexPolymer: OligoConjugateGraphQL | OligoDuplexGraphQL | ComplexPolymerGraphQL
): complexPolymer is OligoDuplexGraphQL {
  return complexPolymer.__typename === 'OligoDuplexNode';
}

export function isOligoConjugateVersion(
  complexPolymerVersion:
    | OligoConjugateVersionGraphQL
    | OligoDuplexVersionGraphQL
    | ComplexPolymerVersionGraphQL
): complexPolymerVersion is OligoConjugateVersionGraphQL {
  return complexPolymerVersion.__typename === 'OligoConjugateVersionNode';
}

export function isOligoDuplexVersion(
  complexPolymerVersion:
    | OligoConjugateVersionGraphQL
    | OligoDuplexVersionGraphQL
    | ComplexPolymerVersionGraphQL
): complexPolymerVersion is OligoDuplexVersionGraphQL {
  return complexPolymerVersion.__typename === 'OligoDuplexVersionNode';
}

export function isOligoConjugateConfigGraphQLType(
  config: ComplexPolymerSchemaConfigGraphQL
): config is OligoConjugateSchemaConfigGraphQL {
  return config.__typename === 'OligoConjugateSchemaConfigNode';
}
export function isOligoDuplexConfigGraphQLType(
  config: ComplexPolymerSchemaConfigGraphQL
): config is OligoDuplexSchemaConfigGraphQL {
  return config.__typename === 'OligoDuplexSchemaConfigNode';
}

export function isMoleculeSimplePolymer(
  simplePolymer: SimplePolymerGraphQL
): simplePolymer is MoleculeSimplePolymerGraphQL {
  return simplePolymer.__typename === 'MoleculePolymerNode';
}

// Keep core logic in sync with benchling/molbio/helm/util.py:get_polymer_ref_number.
function getPolymerRefNumber(polymerRef: string, polymerType: HELMPolymerType): number {
  assert(polymerRef.startsWith(polymerType), 'Expected polymer HELM reference to start with polymer type');
  return parseInt(polymerRef.slice(polymerType.length), 10);
}

// Keep core logic in sync with benchling/molbio/helm/util.py:helm_polymer_type_from_ref.
export function getPolymerTypeFromRef(polymerRef: string): HELMPolymerType {
  const matchedPolymerTypes = Object.values(HELMPolymerType).filter((polymerType) =>
    polymerRef.startsWith(polymerType)
  );
  assert(matchedPolymerTypes.length === 1, 'Expected exactly one HELM polymer type');
  return matchedPolymerTypes[0];
}

export function getNextPolymerRef(
  entityAssociations: EntityAssociations,
  apiId: string,
  complexPolymerType: ComplexPolymerType
): string {
  const polymerType = MODEL_TYPE_TO_HELM_POLYMER_TYPE[APIUtil.apiIdToType(apiId)];
  assert(polymerType != null, 'Expected polymer type to be defined');
  const allPolymerRefsOfType = Object.values(entityAssociations)
    .flat()
    .filter((polymerRef) => polymerRef.startsWith(polymerType));
  const largestPolymerNumberOfType = Math.max(
    ...allPolymerRefsOfType.map((polymerRef) => getPolymerRefNumber(polymerRef, polymerType)),
    polymerType === HELMPolymerType.RNA
      ? complexPolymerType === ComplexPolymerType.OLIGO_CONJUGATE
        ? 1
        : 2
      : 0
  );
  return polymerType + (largestPolymerNumberOfType + 1).toString();
}

function getPolymerRefForOligoConjugate(
  schemaFieldId: string,
  schemaConfig: ComplexPolymerSchemaConfigGraphQL,
  entityAssociations: EntityAssociations,
  apiId: string
): string {
  assert(isOligoConjugateConfigGraphQLType(schemaConfig), 'Expected oligo duplex config');
  // Assigns the backbone strand the polymer ref of RNA1
  if (schemaFieldId === schemaConfig.oligoSchemaField.id) {
    return 'RNA1';
  }
  return getNextPolymerRef(entityAssociations, apiId, ComplexPolymerType.OLIGO_CONJUGATE);
}

function getPolymerRefForOligoDuplex(
  schemaFieldId: string,
  schemaConfig: ComplexPolymerSchemaConfigGraphQL,
  entityAssociations: EntityAssociations,
  apiId: string
): string {
  assert(isOligoDuplexConfigGraphQLType(schemaConfig), 'Expected oligo duplex config');
  // Assigns the forward strand the polymer ref of RNA1, and the reverse strand the polymer ref of RNA2.
  if (schemaFieldId === schemaConfig.forwardStrandSchemaField.id) {
    return 'RNA1';
  } else if (schemaFieldId === schemaConfig.reverseStrandSchemaField.id) {
    return 'RNA2';
  } else {
    return getNextPolymerRef(entityAssociations, apiId, ComplexPolymerType.OLIGO_DUPLEX);
  }
}

export function getNextPolymerRefFromTag(
  complexPolymerConfig: ComplexPolymerSchemaConfigGraphQL,
  schemaFieldId: string,
  targetFile: TargetFile,
  entityAssociations: EntityAssociations
): string {
  const polymerRef =
    complexPolymerConfig != null && isOligoDuplexConfigGraphQLType(complexPolymerConfig)
      ? getPolymerRefForOligoDuplex(schemaFieldId, complexPolymerConfig, entityAssociations, targetFile.id)
      : getPolymerRefForOligoConjugate(
          schemaFieldId,
          complexPolymerConfig,
          entityAssociations,
          targetFile.id
        );

  return polymerRef;
}

export function updateEntityAssociationWithPolymerRef(
  entityId: string,
  polymerRef: string,
  entityAssociations: EntityAssociations
): void {
  if (entityAssociations.hasOwnProperty(entityId)) {
    entityAssociations[entityId].push(polymerRef);
  } else {
    entityAssociations[entityId] = [polymerRef];
  }
}

export function generateEntityAssociationsFromTags(
  complexPolymerTags: Array<CreateComplexPolymerSchemaTag>
): EntityAssociations {
  const entityAssociations: EntityAssociations = {};

  complexPolymerTags.forEach((tag) => {
    (tag.targetFiles ?? []).forEach((file) => {
      assert(
        file.polymerRef != null,
        'Expected polymer ref to be defined for complex polymer component in non-registry create'
      );
      if (entityAssociations.hasOwnProperty(file.id)) {
        entityAssociations[file.id].push(file.polymerRef);
      } else {
        entityAssociations[file.id] = [file.polymerRef];
      }
    });
  });

  return entityAssociations;
}

export function getDisplayResidueIndex(residueIndex: number): number {
  // Residue indices on complex polymers are 0-indexed.
  return residueIndex + 1;
}

export function getConnectionInputs(connections: Array<ConnectionGraphQL>): Array<ComplexPolymerConnection> {
  const connectionInputs = connections.map((connection) => ({
    connection_type: connection.connectionType,
    attachment_style: connection.attachmentStyle,
    source: {
      polymer_ref: connection.source.polymerHelmReference,
      residue_index: connection.source.residueIndex,
      modification_type: connection.source.modificationType,
      r_group: connection.source.rGroup,
    },
    target: {
      polymer_ref: connection.target.polymerHelmReference,
      residue_index: connection.target.residueIndex,
      modification_type: connection.target.modificationType,
      r_group: connection.target.rGroup,
    },
  }));

  return connectionInputs;
}

export function getEntityIds(polymers: Array<SimplePolymerGraphQL>): Array<string> {
  const entityIds = polymers.map((polymer) => {
    assert(
      !isInaccessible(polymer.entity),
      'Component entity must be accessible to edit the complex polymer'
    );
    return polymer.entity.id;
  });

  return entityIds;
}

export function generateEntityAssociationsFromStructure(
  polymers: Array<SimplePolymerGraphQL>
): EntityAssociations {
  const entityAssociations: EntityAssociations = {};
  polymers.reduce((associationsMap, polymer) => {
    const {entity} = polymer;

    assert(!isInaccessible(entity), 'Component entity must be accessible to edit the complex polymer');

    if (associationsMap.hasOwnProperty(entity.id)) {
      associationsMap[entity.id].push(polymer.helmReference);
    } else {
      associationsMap[entity.id] = [polymer.helmReference];
    }

    return associationsMap;
  }, entityAssociations);

  return entityAssociations;
}

export function isComplexPolymer(entityId: string | null): boolean {
  // This checks client state to avoid an async request when rendering the workspace tab.
  // and should only be used to check an entity that has been opened in the UI.
  // This does not technically check whether an entity is a complex polymer, rather it
  // checks that a complex polymer version associated with this entity ID has been opened.
  return (
    entityId != null && getStore().getState().ui.modbio.complexPolymerVersionUIs.hasOwnProperty(entityId)
  );
}

export function isConnectionToMolecule(
  connectionSide: SourceLinkedMoleculeConnectionSideGraphQL | ConjugateConnectionSideGraphQL
): connectionSide is SourceLinkedMoleculeConnectionSideGraphQL {
  return getPolymerTypeFromRef(connectionSide.polymerHelmReference) === HELMPolymerType.CHEM;
}

export function isOligoDuplexForwardStrand(polymerRef: string): boolean {
  return polymerRef === OLIGO_DUPLEX_FORWARD_STRAND_REF;
}

export function isOligoDuplexReverseStrand(polymerRef: string): boolean {
  return polymerRef === OLIGO_DUPLEX_REVERSE_STRAND_REF;
}

export function isBranchedConnection(
  conjugateConnection: ConjugateConnection,
  existingConnections: Array<ComplexPolymerConnection>
): boolean {
  let overlappingConnections = existingConnections.filter(
    (connection) => connection.source.polymer_ref === conjugateConnection.linkedEntityHelmReference
  );

  if (getPolymerTypeFromRef(conjugateConnection.linkedEntityHelmReference!) === HELMPolymerType.RNA) {
    overlappingConnections = overlappingConnections.filter(
      (connection) => connection.source.residue_index === conjugateConnection.linkedEntityResidueIndex
    );
  }

  return overlappingConnections.length > 0;
}

export function complexPolymerInputFromCreateBioentityArgs(
  args: CreateBioentityArgs | CreateAndRegisterBioentityArgs
): ComplexPolymerInput | null {
  if (args.complexPolymerSchemaConfig == null) {
    return null;
  }
  const offset =
    graphqlSchemaInterfaceToComplexPolymerType(args.complexPolymerSchemaConfig) ===
    ComplexPolymerType.OLIGO_DUPLEX
      ? (args.oligoDuplexOffset ?? 0)
      : null;
  return {
    entityAssociations: generateEntityAssociationsFromTags(args.complexPolymerTags!),
    connectionInputs: args.complexPolymerConnections ?? [],
    resolvedAttachmentInputs: args.complexPolymerResolvedAttachments ?? [],
    offset,
  };
}

export function isValidSchemaTypeForComplexPolymerFeatures(schemaId: string): boolean {
  /* Whether the schema type of this schema can support configuration as a complex polymer.

  This does NOT check whether or not the schema has actually been configured as a complex polymer schema.
  */
  return APIUtil.isTagSchema(schemaId);
}

export function isValidEntityTypeForComplexPolymerFeatures(entityId: string): boolean {
  /* Whether the entity type of this entity can support complex polymer features.

  This does NOT check whether or not the entity has actually been configured as a complex polymer.
  */
  return APIUtil.isBasicFolderItem(entityId);
}

export interface RefToEntityIdMap {
  [accessor: string]: string;
}

export function getPolymerRefToEntityIdMap(entityAssociations: EntityAssociations): RefToEntityIdMap {
  const refToEntityIdMap: RefToEntityIdMap = {};

  Object.entries(entityAssociations).forEach(([entityId, polymerRefs]) => {
    polymerRefs.forEach((polymerRef) => {
      refToEntityIdMap[polymerRef] = entityId;
    });
  });
  return refToEntityIdMap;
}

export function getResolvedAttachmentInput(
  resolvedAttachment: ResolvedAttachmentGraphQL
): ResolvedAttachmentInput {
  // Format resolved attachment in the format the backend endpoints are expecting
  return {
    molfile: resolvedAttachment.molfile,
    connections_involved: getConnectionInputs(resolvedAttachment.connectionsInvolved),
  };
}

export function getExistingResolvedAttachmentInputs(
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>
): Array<ResolvedAttachmentInput> {
  return resolvedAttachments.flatMap((resolvedAttachment) =>
    resolvedAttachment.molfile != null &&
    resolvedAttachment.connectionsInvolved.every(
      (c) => c.attachmentStyle === ComplexPolymerAttachmentStyle.DRAWN
    )
      ? [getResolvedAttachmentInput(resolvedAttachment)]
      : []
  );
}

export function resolvedAttachmentContainsHelmReference(
  resolvedAttachment: ResolvedAttachmentGraphQL,
  helmReference: string
): boolean {
  return resolvedAttachment.polymers.some((polymer) => polymer.helmReference === helmReference);
}

export function isPolymerBackboneStrand(complexPolymerType: ComplexPolymerType, polymerRef: string): boolean {
  if (complexPolymerType === ComplexPolymerType.OLIGO_CONJUGATE) {
    return polymerRef === OLIGO_CONJUGATE_BACKBONE_STRAND_REF;
  } else if (complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX) {
    return polymerRef === OLIGO_DUPLEX_FORWARD_STRAND_REF || polymerRef === OLIGO_DUPLEX_REVERSE_STRAND_REF;
  }
  return false;
}

export function getResolvedAttachmentsForOligoDuplex(
  forwardStrand: OligoDuplexOligoStrandGraphQL,
  reverseStrand: OligoDuplexOligoStrandGraphQL
): Array<ResolvedAttachmentGraphQL> {
  // TODO: Conjugate-conjugate only resolved attachments are duplicated on the forward and reverse strands for
  // oligo duplexes. This function filters out duplicate resolved attachments for the forward and reverse
  // strands.
  const seenIds: Set<string> = new Set();
  const forwardStrandResolvedAttachment = forwardStrand.resolvedAttachments.filter((resolvedAttachment) => {
    // ResolvedAttachmentNode's ID starts with the strand ref, followed by a unique ID composed of polymer
    // refs and version ID. We use the unique portion of this to dedupe resolved attachments
    seenIds.add(resolvedAttachment.id.substring(4));
    return resolvedAttachment.chemicalStructureImage != null;
  });
  // Filter out conjugate only resolved attachments from the reverse strand, as they are captured from the
  // forward strand
  const dedupedReverseStrandResolvedAttachments = reverseStrand.resolvedAttachments.filter(
    (resolvedAttachment) =>
      resolvedAttachment.chemicalStructureImage && !seenIds.has(resolvedAttachment.id.substring(4))
  );
  return forwardStrandResolvedAttachment.concat(dedupedReverseStrandResolvedAttachments);
}

export function getGQLComplexPolymerType(complexPolymerType: ComplexPolymerType): GQLComplexPolymerType {
  if (complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX) {
    return GQLComplexPolymerType.OLIGO_DUPLEX;
  } else if (complexPolymerType === ComplexPolymerType.OLIGO_CONJUGATE) {
    return GQLComplexPolymerType.OLIGO_CONJUGATE;
  }
  assert(complexPolymerType === ComplexPolymerType.GENERIC, 'Expected valid complex polymer type');
  return GQLComplexPolymerType.GENERIC;
}

export function getBackbonePolymerFromResolvedAttachment(
  complexPolymerType: ComplexPolymerType,
  resolvedAttachment: ResolvedAttachmentGraphQL
): OligoSimplePolymerGraphQL | null {
  return (
    resolvedAttachment.polymers.find((polymer): polymer is OligoSimplePolymerGraphQL =>
      isPolymerBackboneStrand(complexPolymerType, polymer.helmReference)
    ) ?? null
  );
}
